import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/mechanic/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Sparkles, Plus } from "lucide-react";

/**
 * Identifix-style "+ Add service" modal. Three sources:
 *   - Canned:  reusable service templates (labor + parts + oil bundles)
 *   - Past:    services previously done on THIS vehicle (re-run in one click)
 *   - Scratch: appends an empty labor line so the tech can build from zero
 *
 * On pick, calls `onApply({labor_lines, parts_lines, oil_lines, is_oil_change, complaint, shop_supply_fee})`
 * with the lines to append to the currently open WO. Existing WO lines are
 * preserved (append semantics).
 */
export default function AddServiceDialog({
  open, onOpenChange, onApply, vehicleSource, vehicleRefId,
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data: canned = [], isLoading: cLoad } = useQuery({
    queryKey: ["mech-canned"],
    enabled: open,
    queryFn: async () => (await api.get("/canned-jobs")).data,
  });
  const { data: past = [], isLoading: pLoad } = useQuery({
    queryKey: ["mech-past-adds", vehicleSource, vehicleRefId],
    enabled: !!(open && vehicleSource && vehicleRefId),
    queryFn: async () =>
      (await api.get("/work-orders", { params: { vehicle_source: vehicleSource, vehicle_ref_id: vehicleRefId } })).data,
  });

  const bump = useMutation({ mutationFn: (id) => api.post(`/canned-jobs/${id}/use`) });

  const filteredCanned = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return canned;
    return canned.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.category || "").toLowerCase().includes(s));
  }, [q, canned]);

  const applyCanned = (c) => {
    onApply({
      labor_lines: (c.labor_lines || []).map((l) => ({ ...l, job_group: l.job_group || c.name })),
      parts_lines: (c.parts_lines || []).map((l) => ({ ...l, job_group: l.job_group || c.name })),
      oil_lines: (c.oil_lines || []).map((l) => ({ ...l })),
      is_oil_change: !!c.is_oil_change,
      complaint: c.default_complaint || "",
      shop_supply_fee: c.shop_supply_fee || 0,
    });
    bump.mutate(c.id);
    qc.invalidateQueries({ queryKey: ["mech-canned"] });
    toast.success(`Added "${c.name}"`);
    onOpenChange(false);
  };

  const applyPast = (w) => {
    const total = (w.labor_lines || []).length + (w.parts_lines || []).length + (w.oil_lines || []).length;
    onApply({
      labor_lines: (w.labor_lines || []).map((l) => ({ ...l })),
      parts_lines: (w.parts_lines || []).map((l) => ({ ...l })),
      oil_lines: (w.oil_lines || []).map((l) => ({ ...l })),
      is_oil_change: !!w.is_oil_change,
      complaint: w.complaint || "",
      shop_supply_fee: w.shop_supply_fee || 0,
    });
    toast.success(`Copied ${total} line(s)`);
    onOpenChange(false);
  };

  const applyScratch = () => {
    onApply({
      labor_lines: [{ description: "", hours: 0, rate_cost: 0, rate_charge: 0, tech_id: "", tech_name: "" }],
      parts_lines: [],
      oil_lines: [],
    });
    toast.success("Empty service line added");
    onOpenChange(false);
  };

  const laborTotal = (c) => (c.labor_lines || []).reduce((s, l) => s + Number(l.hours || 0) * Number(l.rate_charge || l.rate_cost || 0), 0);
  const partsTotal = (c) => (c.parts_lines || []).reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || l.unit_cost || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="add-service-dialog">
        <DialogHeader><DialogTitle>Add service</DialogTitle></DialogHeader>
        <Tabs defaultValue="canned">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="canned" data-testid="tab-canned">Canned jobs</TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">Past services</TabsTrigger>
            <TabsTrigger value="scratch" data-testid="tab-scratch">Scratch</TabsTrigger>
          </TabsList>

          <TabsContent value="canned" className="mt-4">
            <div className="relative mb-3">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search canned jobs…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="canned-search" />
            </div>
            {cLoad ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Loading…</p>
            ) : filteredCanned.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <p className="text-sm text-muted-foreground">No canned jobs {q ? "match your search" : "yet"}.</p>
                <p className="text-xs text-muted-foreground">Build one from Shop Settings → Canned jobs to save minutes on repeat work.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border border border-border rounded-md max-h-80 overflow-y-auto" data-testid="canned-list">
                {filteredCanned.map((c) => {
                  const est = laborTotal(c) + partsTotal(c);
                  return (
                    <li key={c.id} className="px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-secondary/40 cursor-pointer" onClick={() => applyCanned(c)} data-testid={`canned-${c.id}`}>
                      <div className="min-w-0">
                        <div className="font-heading truncate flex items-center gap-2">
                          {c.name}
                          {c.category && <Badge variant="secondary" className="text-[10px]">{c.category}</Badge>}
                          {c.is_oil_change && <Badge className="text-[10px] bg-amber-500 text-white">Oil</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {(c.labor_lines || []).length} labor · {(c.parts_lines || []).length} parts
                          {c.use_count > 0 && ` · used ${c.use_count}×`}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-mono-data text-sm">{money(est)}</div>
                        <div className="text-[10px] text-muted-foreground">est.</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-4">
            {!vehicleRefId ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Select a vehicle first to see its past services.</p>
            ) : pLoad ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Loading…</p>
            ) : past.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No prior services on this vehicle yet.</p>
            ) : (
              <ul className="divide-y divide-border border border-border rounded-md max-h-80 overflow-y-auto" data-testid="past-list">
                {past.slice(0, 20).map((w) => {
                  const total = (w.labor_lines || []).length + (w.parts_lines || []).length + (w.oil_lines || []).length;
                  return (
                    <li key={w.id} className="px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-secondary/40 cursor-pointer" onClick={() => applyPast(w)} data-testid={`past-${w.id}`}>
                      <div className="min-w-0">
                        <div className="font-heading truncate">{w.complaint || w.diagnosis || `${total} line${total === 1 ? "" : "s"}`}</div>
                        <div className="text-[11px] text-muted-foreground font-mono truncate">{w.service_date || w.created_at?.slice(0, 10)} · {w.status || "—"} · {total} line{total === 1 ? "" : "s"}</div>
                      </div>
                      <Sparkles className="h-4 w-4 text-muted-foreground shrink-0" />
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="scratch" className="mt-4">
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted-foreground">Build from zero — adds an empty labor line so you can type the description, hours, rate, and any parts by hand.</p>
              <Button onClick={applyScratch} data-testid="scratch-add-btn"><Plus className="h-4 w-4 mr-1" />Add empty labor line</Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
